import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, ResponsiveImage, UserReviewsSummary } from '../../components';
import ListingLink from '../ListingLink/ListingLink';
import { useUserReviews } from '../../hooks/useUserReviews';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  // Hide price completely from listings page
  return null;
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showTitle = true,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const listingType = publicData?.listingType;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
  listingType === 'sell-services'
    ? author.profileImage
    : currentListing.images?.[0] || null;

  
  // Fetch real user reviews
  const { reviews: userReviews, loading: reviewsLoading } = useUserReviews(author.id.uuid, REVIEW_TYPE_OF_PROVIDER);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
 const variants = firstImage?.attributes?.variants
  ? Object.keys(firstImage.attributes.variants).filter(k =>
      k.startsWith(variantPrefix)
    )
  : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const authorInfoClassName = classNames(css.authorInfo, {
    [css.authorInfoStandalone]: !showTitle,
  });

  return (
    <ListingLink className={classes} listing={currentListing}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        {/*  <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />*/}
        <div className={css.mainInfo}>
          {showTitle ? (
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
          ) : null}
          {showAuthorInfo ? (
            <div className={authorInfoClassName}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              {/* User reviews summary */}
              <UserReviewsSummary 
                reviews={userReviews} 
                reviewType={REVIEW_TYPE_OF_PROVIDER}
                loading={reviewsLoading}
                className={css.userReviewsSummary}
              />
            </div>
          ) : null}
        </div>
      </div>
    </ListingLink>
  );
};

export default ListingCard;
